import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import {
  X,
  Calendar,
  Trophy,
  Medal,
  ExternalLink,
  ClipboardList,
  Gift,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { GiveawayContest } from "types/giveaway";
import { toDate } from "pages/Admin/giveawayManagement/giveawayFormUtils";
import { InstagramIcon, LinkedInIcon } from "components/common/SocialIcons";

interface GiveawayContestModalProps {
  isOpen: boolean;
  onClose: () => void;
  contests: GiveawayContest[];
}

const formatDateTime = (value?: string | null) => {
  const date = toDate(value);
  return date ? format(date, "dd MMM yyyy, hh:mm a") : "-";
};

const GiveawayContestModal: React.FC<GiveawayContestModalProps> = ({
  isOpen,
  onClose,
  contests,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Reset to the first contest each time the modal is (re)opened.
  useEffect(() => {
    if (isOpen) setActiveIndex(0);
  }, [isOpen]);

  if (!isOpen || contests.length === 0) return null;

  const contest = contests[activeIndex % contests.length];
  const hasMultiple = contests.length > 1;

  const goPrev = () =>
    setActiveIndex((i) => (i - 1 + contests.length) % contests.length);
  const goNext = () => setActiveIndex((i) => (i + 1) % contests.length);

  const parsedRules = contest.rules?.trim()
    ? contest.rules
        .split("\n")
        .map((r) => r.replace(/^[•\-*]\s*/, "").trim())
        .filter(Boolean)
    : [];

  const hasInstagram = !!contest.instagramUrl?.trim();
  const hasLinkedIn = !!contest.linkedInUrl?.trim();
  const hasSocial = hasInstagram || hasLinkedIn;

  const has2ndPrize = !!contest.reward2nd?.trim();
  const has3rdPrize = !!contest.reward3rd?.trim();
  const hasAdditionalWinnerReward =
    !!contest.additionalWinnersCount && contest.additionalWinnersCount > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="giveaway-modal-title"
    >
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
      {/* Fixed Header Bar (does not scroll) */}
      <div className="relative shrink-0 p-6 sm:p-8 pb-0">
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 sm:top-8 sm:right-8 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-800 flex items-center justify-center transition-colors"
          aria-label="Close dialog"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center gap-4">
          {contest.thumbnailUrl ? (
            <img
              src={contest.thumbnailUrl}
              alt={contest.contestName || "Giveaway Thumbnail"}
              className="w-14 h-14 rounded-2xl object-cover border border-violet-100 shadow-lg shrink-0"
            />
          ) : (
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-[#E11D48] via-[#EC4899] to-[#8B5CF6] flex items-center justify-center text-white shadow-lg shrink-0">
              <Gift className="w-7 h-7" />
            </div>
          )}
          <div className="pr-8 min-w-0">
            <h2
              id="giveaway-modal-title"
              className="text-xl sm:text-2xl font-bold text-grease-100 tracking-tight truncate"
            >
              {contest.contestName || "Giveaway Contest"}
            </h2>
            <p className="text-xs sm:text-sm text-lightgray-200 mt-0.5">
              Participate now and stand a chance to win exciting prizes!
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="p-6 sm:p-8 pt-4 mr-1 mb-2 overflow-y-auto">
        {/* Prev/Next + dot indicators when there are multiple contests */}
        {hasMultiple && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={goPrev}
              aria-label="Previous contest"
              className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors shrink-0"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-1.5">
              {contests.map((c, idx) => (
                <button
                  key={c.contestId}
                  type="button"
                  onClick={() => setActiveIndex(idx)}
                  aria-label={`Show contest ${idx + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    idx === activeIndex
                      ? "w-5 bg-violet-600"
                      : "w-1.5 bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={goNext}
              aria-label="Next contest"
              className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors shrink-0"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Contest Period Banner */}
        <div className="mt-0 rounded-2xl bg-[#F5F3FF] border border-[#DDD6FE] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center text-violet-700 shrink-0 mt-0.5">
              <Calendar className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                Contest Period
              </p>
              <p className="text-xs sm:text-sm font-semibold text-grease-100 mt-0.5">
                {formatDateTime(contest.startDate)} to{" "}
                {formatDateTime(contest.endDate)}
              </p>
            </div>
          </div>
        </div>

        {/* Gift Prizes & Social Channels */}
        <div
          className={`mt-6 grid gap-5 ${
            hasSocial ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"
          }`}
        >
          {/* Left Column: Gift Prizes */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
              Gift Prizes
            </p>

            <div className="space-y-3">
              {/* 1st Prize */}
              <div className="flex items-center gap-3 p-3.5 rounded-xl border border-gray-200 bg-white hover:border-violet-300 transition-colors shadow-sm">
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold shrink-0">
                  <Trophy className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] text-gray-400 font-semibold uppercase">
                    1st Prize
                  </p>
                  <p className="text-xs sm:text-sm font-bold text-grease-100 truncate">
                    {contest.reward1st || "-"}
                  </p>
                </div>
              </div>

              {/* 2nd Prize */}
              {has2ndPrize && (
                <div className="flex items-center gap-3 p-3.5 rounded-xl border border-gray-200 bg-white hover:border-violet-300 transition-colors shadow-sm">
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold shrink-0">
                    <Medal className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 font-semibold uppercase">
                      2nd Prize
                    </p>
                    <p className="text-xs sm:text-sm font-bold text-grease-100 truncate">
                      {contest.reward2nd || "-"}
                    </p>
                  </div>
                </div>
              )}

              {/* 3rd Prize */}
              {has3rdPrize && (
                <div className="flex items-center gap-3 p-3.5 rounded-xl border border-gray-200 bg-white hover:border-violet-300 transition-colors shadow-sm">
                  <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold shrink-0">
                    <Medal className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 font-semibold uppercase">
                      3rd Prize
                    </p>
                    <p className="text-xs sm:text-sm font-bold text-grease-100 truncate">
                      {contest.reward3rd || "-"}
                    </p>
                  </div>
                </div>
              )}

              {/* Additional Winner Token Reward */}
              {hasAdditionalWinnerReward && (
                <div className="flex items-center justify-between p-3.5 rounded-xl border border-gray-200 bg-white shadow-sm">
                  <p className="text-[10px] text-gray-400 font-semibold uppercase">
                    Additional Winner Token Reward
                  </p>
                  <span className="text-xs font-bold text-violet-700 bg-violet-50 border border-violet-200 px-2.5 py-0.5 rounded-full shrink-0">
                    {contest.tokenRewardAdditionalWinners ?? 0} Tokens
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Social Channels */}
          {hasSocial && (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                Follow &amp; Participate
              </p>

              <div className="space-y-3">
                {/* Instagram */}
                {hasInstagram && (
                  <a
                    href={contest.instagramUrl!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3.5 rounded-xl border border-gray-200 bg-white hover:border-pink-400 hover:bg-pink-50/20 transition-all shadow-sm group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center text-white shrink-0 shadow-sm">
                        <InstagramIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 font-semibold uppercase">
                          Instagram
                        </p>
                        <p className="text-xs sm:text-sm font-bold text-grease-100 truncate group-hover:text-pink-600 transition-colors">
                          {contest.instagramUrl!.replace(
                            /^https?:\/\/(www\.)?/,
                            "",
                          )}
                        </p>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-pink-600 shrink-0" />
                  </a>
                )}

                {/* LinkedIn */}
                {hasLinkedIn && (
                  <a
                    href={contest.linkedInUrl!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3.5 rounded-xl border border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50/20 transition-all shadow-sm group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-[#0A66C2] flex items-center justify-center text-white shrink-0 shadow-sm">
                        <LinkedInIcon className="w-[18px] h-[18px] text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] text-gray-400 font-semibold uppercase">
                          LinkedIn
                        </p>
                        <p className="text-xs sm:text-sm font-bold text-grease-100 truncate group-hover:text-[#0A66C2] transition-colors">
                          {contest.linkedInUrl!.replace(
                            /^https?:\/\/(www\.)?/,
                            "",
                          )}
                        </p>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#0A66C2] shrink-0" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Rules Section */}
        <div className="mt-6 rounded-2xl bg-gray-50 border border-gray-200/80 p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList className="w-4 h-4 text-gray-600" />
            <h3 className="text-xs font-bold text-grease-100 uppercase tracking-wider">
              Rules
            </h3>
          </div>

          {parsedRules.length > 0 ? (
            <ul className="space-y-1.5">
              {parsedRules.map((rule, idx) => (
                <li
                  key={idx}
                  className="text-xs text-gray-600 flex items-start gap-2 leading-relaxed"
                >
                  <span className="text-violet-600 font-bold text-sm leading-none mt-0.5">
                    •
                  </span>
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-gray-500 italic">
              No specific rules recorded for this contest.
            </p>
          )}
        </div>

        {/* Footer Button */}
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto min-w-[200px] px-8 py-3 bg-[#FBBF24] hover:bg-[#F59E0B] text-white font-bold text-sm rounded-xl transition-all shadow-md hover:shadow-lg"
          >
            Got It
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default GiveawayContestModal;

